import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";
import { useTranslation } from "react-i18next";
import Axios from "axios";

import { Button, ButtonToolbar, Heading, Table, Text } from "rsuite";
import { SortType } from "rsuite-table";

import i18n from "utils/i18n";
import constants from "utils/constants";
import { getModalTableHeight } from "utils/measurement-helper";
import { getSorted, Paths } from "utils/sort-halper";

import { OperationErrorLog } from "store/common/operation-types";
import { Mailbox } from "store/environments/tenants/mailboxes/mailboxes-types";

import { loadMailboxesErrors } from "api/environments/tenants/mailboxes-api";
import { getErrorMessage } from "api/defaults";

import { AppDispatch, RootState } from "store/store";
import { getActiveEnvironmentId } from "store/environments/environment-selectors";
import { getTenantId } from "store/environments/tenants/tenants-selectors";

import { setModalLoaderVisibility } from "store/common/modal-window/modal-window-slices";
import { setErrorResponseMessageForModal } from "store/common/initialization/initialization-actions";

import ModalContainer from "components/Common/Modal/ModalContainer";
import PopoverCell from "components/Common/PopoverCell/PopoverCell";

import "./ErrorLogs.less";

const useAppDispatch = () => useDispatch<AppDispatch>();

interface Props {
    title?: string;
    open: boolean;

    setOpen?: (open: boolean) => void;
}

export type TableColumn = Paths<OperationErrorLog<Mailbox>>;

const columns: Record<
    keyof Omit<OperationErrorLog<Mailbox>, "editor" | "editionDate">,
    TableColumn
> = {
    data: "data.address",
    errorMessage: "errorMessage",
};

const ErrorLogs: React.FC<Props> = ({
    title = i18n.t("tenantConfig.captureMailboxes.errorLogsTitle"),
    open,
    setOpen,
}: Props) => {
    const cancelTokenSource = Axios.CancelToken.source();

    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const activeEnvironmentId = useSelector((state: RootState): string =>
        getActiveEnvironmentId(state)
    );

    const activeTenantId = useSelector((state: RootState): string =>
        getTenantId(state)
    );

    const [errorLogs, setErrorLogs] = useImmer<
        ReadonlyArray<OperationErrorLog<Mailbox>>
    >([]);

    const [sortColumn, setSortColumn] = useState<TableColumn>(columns.data);
    const [sortType, setSortType] = useState<SortType>("desc");

    const tableHeight = getModalTableHeight(errorLogs.length);

    const fetchData = () => {
        dispatch(setModalLoaderVisibility(true));
        dispatch(setErrorResponseMessageForModal(""));

        Promise.all([fetchMailboxesErrors()])
            .then((value) => {
                if (value.some((v) => typeof v == "string")) {
                    const errors = new Set<string>(
                        value.filter((v) => typeof v == "string")
                    );

                    const message = [...errors].join(". ");
                    dispatch(setErrorResponseMessageForModal(message));
                }
            })
            .finally(() => {
                dispatch(setModalLoaderVisibility(false));
            });
    };

    const fetchMailboxesErrors = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadMailboxesErrors(
                activeEnvironmentId,
                activeTenantId,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setErrorLogs(
                    getSorted(
                        [...payload],
                        sortColumn as keyof OperationErrorLog<Mailbox>,
                        sortType
                    )
                );

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!Axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const handleSortColumn = (sortColumn: string, sortType: any) => {
        setSortColumn(sortColumn as TableColumn);
        setSortType(sortType);

        setErrorLogs((draft) =>
            getSorted(draft, sortColumn as TableColumn, sortType)
        );
    };

    const handleModalClose = () => {
        setSortColumn(columns.data);
        setSortType("desc");

        setOpen?.(false);
    };

    useEffect(() => {
        if (open && activeEnvironmentId && activeTenantId) {
            fetchData();
        }
    }, [open, activeEnvironmentId, activeTenantId]);

    const header = <Heading level={1}>{title}</Heading>;
    const footer = (
        <ButtonToolbar>
            <Button
                style={{ height: "2.5rem", width: "5rem" }}
                type="submit"
                appearance="ghost"
                onClick={handleModalClose}
            >
                {t("deliverables.packages.history.close")}
            </Button>
        </ButtonToolbar>
    );

    return (
        <ModalContainer
            className={"mailboxes-error-logs__modal"}
            open={open}
            header={header}
            footer={footer}
            onClose={handleModalClose}
        >
            <Table
                shouldUpdateScroll={false}
                virtualized={true}
                data={errorLogs}
                height={tableHeight}
                sortColumn={sortColumn}
                sortType={sortType}
                onSortColumn={handleSortColumn}
            >
                <Table.Column sortable={true} flexGrow={2}>
                    <Table.HeaderCell>
                        {t(`tenantConfig.captureMailboxes.table.address`)}
                    </Table.HeaderCell>
                    <Table.Cell dataKey={columns.data}>
                        {(rowData: OperationErrorLog<Mailbox>) => (
                            <Text>{rowData.data.address}</Text>
                        )}
                    </Table.Cell>
                </Table.Column>

                <Table.Column flexGrow={3}>
                    <Table.HeaderCell>
                        {t(`tenantConfig.captureMailboxes.table.failureReason`)}
                    </Table.HeaderCell>
                    <Table.Cell dataKey={columns.errorMessage}>
                        {(rowData: OperationErrorLog<Mailbox>) => (
                            <PopoverCell
                                className="error-text"
                                dataKey={columns.errorMessage}
                                rowData={rowData}
                            />
                        )}
                    </Table.Cell>
                </Table.Column>
            </Table>
        </ModalContainer>
    );
};

export default ErrorLogs;
